/**
 * AdminEmailBroadcast — admin-composed mass email blast.
 *
 * Sits inside Admin → Email Templates → "Send broadcast" tab. Operator picks
 * an audience (all / complete-profile / incomplete-profile / founding patrons
 * / waitlist), optionally narrows by sector, composes subject + body, hits
 * Preview (dry-run) to see a sample render + recipient count, then Send to
 * queue. Backend inserts rows into email_outbox the same way transactional
 * emails are queued — Resend worker picks them up.
 *
 * Why this isn't a stored "template" (like waitlist_welcome): broadcasts are
 * one-off content the admin wants to ship once. Persisting them as templates
 * would clutter the template list. Each send is logged in `email_broadcasts`
 * for the audit history panel.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/date";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const AUDIENCE_LABELS = {
  all: "Every active patron",
  complete_profile: "Patrons with completed profile",
  incomplete_profile: "Patrons with incomplete profile",
  founding_patrons: "Founding Patrons only",
  password_pending: "Legacy waitlist — needs to set password",
  waitlist: "Confirmed waitlist signups (legacy table)",
};

// Recommended starter templates per audience. The admin can edit them in the
// textbox before sending. The merge variables ({{first_name}}, {{set_password_url}},
// {{panelist_url}}) are substituted per recipient at send time.
const SUGGESTED_TEMPLATES = {
  password_pending: {
    subject: "Hi {{first_name}}, set your password to unlock oodel.",
    body: `<p>Hi {{first_name}},</p>
<p>You joined the oodel. waitlist earlier — thank you for that. We've now opened the platform and your account is ready.</p>
<p>The only thing left to do is <strong>set a password</strong>. It takes one click:</p>
<p><a href="{{set_password_url}}" style="display:inline-block;background:#16A34A;color:#fff;padding:12px 22px;text-decoration:none;font-weight:600;">Set my password →</a></p>
<p>Once you're in, finish your quick profile (2 minutes) and you'll earn a <strong>200-point welcome bonus</strong> plus access to all live surveys that match your interests.</p>
<p>Looking forward to seeing you on the panel.</p>
<p>— Team oodel.</p>`,
  },
  incomplete_profile: {
    subject: "Quick favour — finish your oodel. profile",
    body: `<p>Hi {{first_name}},</p>
<p>You signed up for oodel. — Pakistan's market intelligence platform — but we noticed your profile isn't complete yet.</p>
<p><strong>Why this matters:</strong> we use your sectors of interest + demographics to match you with surveys that actually relate to you. Without a complete profile, you won't be invited to take any survey — and you'll miss the points and rewards that come with each one.</p>
<p>It only takes 2 minutes:</p>
<p><a href="{{panelist_url}}/profile" style="display:inline-block;background:#16A34A;color:#fff;padding:12px 22px;text-decoration:none;font-weight:600;">Complete my profile →</a></p>
<p>Thanks for being on the panel. Your voice shapes Pakistan's data narrative.</p>
<p>— Team oodel.</p>`,
  },
};

export default function AdminEmailBroadcast() {
  const [counts, setCounts] = useState({});
  const [audience, setAudience] = useState("all");
  const [sector, setSector] = useState("");
  const [sectorOpts, setSectorOpts] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => {
    api.get("/admin/email-templates/broadcast/history").then((r) => setHistory(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/email-templates/broadcast/audience-counts").then((r) => setCounts(r.data || {})).catch(() => {});
    api.get("/sectors").then((r) => setSectorOpts(r.data || [])).catch(() => {});
    loadHistory();
  }, []);

  const dryRun = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-templates/broadcast", {
        subject, body_html: body, audience, sector: sector || null, dry_run: true,
      });
      setPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!preview) { toast.error("Hit 'Preview' first — it shows you exactly what will get sent and to how many people."); return; }
    if (!window.confirm(`Queue ${preview.recipient_count} email${preview.recipient_count === 1 ? "" : "s"}?\n\nThis cannot be un-sent.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-templates/broadcast", {
        subject, body_html: body, audience, sector: sector || null, dry_run: false,
      });
      toast.success(`Queued ${data.queued} email${data.queued === 1 ? "" : "s"}.`);
      setSubject(""); setBody(""); setPreview(null);
      loadHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-broadcast">
      <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-xs text-amber-900">
        <b>Mass broadcasts are NOT for transactional content.</b> Use the existing template editor for sign-up/welcome/reset flows. Broadcasts are for one-off announcements: promotions, notices, product updates, raffle reminders, etc. Sends through your configured Resend account using the brand sender ({/* settings.from_email shown via the template settings tab */}<span className="font-mono">Email Templates → Sender</span>).
      </div>

      <div className="bg-white border-2 border-black p-5">
        <div className="overline mb-3">1. Pick your audience</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {Object.entries(AUDIENCE_LABELS).map(([k, label]) => (
            <label key={k} className={`flex items-center gap-3 p-3 border-2 cursor-pointer ${audience === k ? "border-mango bg-mango/5" : "border-neutral-200"}`} data-testid={`broadcast-audience-${k}`}>
              <input type="radio" name="audience" value={k} checked={audience === k} onChange={() => { setAudience(k); setPreview(null); }} />
              <div className="flex-1">
                <div className="font-bold">{label}</div>
                <div className="text-xs text-neutral-500 font-mono">{counts[k] != null ? `${counts[k]} recipients` : "loading…"}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="mt-4">
          <Label className="overline text-xs">Narrow by sector of interest <span className="text-neutral-400 normal-case">(optional · only applies to patron audiences)</span></Label>
          <select
            value={sector}
            onChange={(e) => { setSector(e.target.value); setPreview(null); }}
            disabled={audience === "waitlist"}
            className="rounded-none border-2 border-neutral-300 px-3 py-2 bg-white text-sm w-full md:w-auto disabled:bg-neutral-100"
            data-testid="broadcast-sector"
          >
            <option value="">All sectors</option>
            {sectorOpts.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border-2 border-black p-5">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="overline">2. Compose your email</div>
          {SUGGESTED_TEMPLATES[audience] && (
            <button
              type="button"
              onClick={() => {
                const t = SUGGESTED_TEMPLATES[audience];
                if (subject || body) {
                  if (!window.confirm("Replace current subject + body with the suggested template?")) return;
                }
                setSubject(t.subject);
                setBody(t.body);
                setPreview(null);
              }}
              data-testid="broadcast-use-template"
              className="text-xs underline text-mango hover:text-mango/80 whitespace-nowrap"
            >
              Use suggested template for this audience →
            </button>
          )}
        </div>
        <p className="text-xs text-neutral-500 mb-3">Merge variables: <span className="font-mono">{"{{name}}"}</span> (full name) · <span className="font-mono">{"{{first_name}}"}</span> · <span className="font-mono">{"{{email}}"}</span></p>
        <Label className="overline text-xs">Subject</Label>
        <Input
          value={subject}
          onChange={(e) => { setSubject(e.target.value); setPreview(null); }}
          placeholder="Hey {{first_name}}, our biggest survey just dropped"
          className="rounded-none mb-3"
          data-testid="broadcast-subject"
        />
        <Label className="overline text-xs">Body (HTML allowed — paste your full HTML here)</Label>
        <div className="text-xs text-neutral-600 mb-2 bg-neutral-50 border-l-4 border-mango p-2">
          <b>Merge variables:</b> <code>{"{{first_name}}"}</code>, <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>, <code>{"{{panelist_url}}"}</code>, and <code>{"{{set_password_url}}"}</code> — the last one resolves to a per-recipient one-click link that takes legacy-waitlist users straight to the Set Password screen with their email locked in. Use this when targeting the <i>Legacy waitlist — needs to set password</i> audience.
        </div>
        <textarea
          value={body}
          onChange={(e) => { setBody(e.target.value); setPreview(null); }}
          placeholder={"<p>Hi {{first_name}},</p>\n<p>Welcome back — claim your oodel account in one click:</p>\n<p><a href=\"{{set_password_url}}\">Set your password →</a></p>"}
          rows={12}
          className="w-full px-3 py-2 border-2 border-neutral-300 rounded-none text-sm font-mono"
          data-testid="broadcast-body"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={dryRun} disabled={busy} variant="outline" className="rounded-none" data-testid="broadcast-preview">{busy ? "…" : "Preview"}</Button>
        <Button onClick={send} disabled={busy || !preview} className="bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none" data-testid="broadcast-send">{busy ? "…" : `Send to ${preview?.recipient_count ?? "?"}`}</Button>
        {preview && (
          <span className="text-xs text-neutral-600">Will queue <b>{preview.recipient_count}</b> email{preview.recipient_count === 1 ? "" : "s"} to <b>{AUDIENCE_LABELS[preview.audience]}</b>{preview.sector ? ` · ${preview.sector}` : ""}.</span>
        )}
      </div>

      {preview && (
        <div className="bg-neutral-900 text-neutral-200 border-2 border-mango p-5" data-testid="broadcast-sample">
          <div className="overline text-mango mb-3">Sample render — first recipient</div>
          <div className="text-xs text-neutral-400 font-mono">To: {preview.sample_render.to}</div>
          <div className="text-sm font-bold mt-2 mb-3">Subject: {preview.sample_render.subject}</div>
          <div className="bg-white text-neutral-900 p-4 text-sm" dangerouslySetInnerHTML={{ __html: preview.sample_render.body_html }} />
        </div>
      )}

      <div className="bg-white border-2 border-neutral-200 p-5">
        <div className="overline mb-3">Broadcast history (last 50)</div>
        {history.length === 0 ? (
          <div className="text-sm text-neutral-500 italic">No broadcasts sent yet.</div>
        ) : (
          <div className="space-y-2 text-sm">
            {history.map((h) => (
              <div key={h.id} className="border-b border-neutral-100 pb-2 flex items-center justify-between">
                <div>
                  <div className="font-bold">{h.subject}</div>
                  <div className="text-xs text-neutral-500 font-mono">{formatDateTime(h.created_at)} · {AUDIENCE_LABELS[h.audience] || h.audience}{h.sector ? ` · ${h.sector}` : ""} · {h.recipient_count} recipients</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
